import {
  Alert,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  ButtonGroup,
  Divider,
  Flex,
  FormControl,
  HStack,
  IconButton,
  Input,
  List,
  ListItem,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Popover,
  PopoverAnchor,
  PopoverContent,
  Portal,
  Tooltip,
  useColorMode,
  useToken,
} from '@chakra-ui/react';
import {
  faCheck,
  faEllipsisH,
  faEye,
  faEyeSlash,
  faGripVertical,
  faPen,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { Reorder } from 'framer-motion';
import { isEmpty } from 'lodash';
import { useEffect, useRef, useState, memo } from 'react';
import { useForm } from 'react-hook-form';
import invariant from 'tiny-invariant';
import { useTranslation } from 'react-i18next';
import {
  ColumnType,
  OrderColumnDeepFragment,
  OrderColumnInput,
} from '../../gql/graphql';
import PlaceholderText from '../text/PlaceholderText';
import { getTranslatedDefaultColumnName } from '../project/aggregated-order-panel/utils';
import DeleteMenuItem from './DeleteMenuItem';
import SparkelIcon from './icon/SparkelIcon';

export type Column = Omit<
  OrderColumnDeepFragment,
  'orderId' | 'columnType' | 'showTotal'
> & {
  columnType?: ColumnType;
};

type ColumnManagementModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (columns: OrderColumnInput[]) => Promise<void> | void;
  orderColumns: Column[];
  deleteCustomColumn?: (columnId: string) => Promise<void>;
  resetColumnWidths?: () => void;
  columnNameMapping?: Record<string, { id: string; newName: string }>;
};

export default memo(function ColumnManagementModal({
  isOpen,
  onClose,
  onSubmit,
  orderColumns,
  deleteCustomColumn,
  resetColumnWidths,
  columnNameMapping,
}: ColumnManagementModalProps): React.ReactElement {
  const [items, setItems] = useState<Column[]>(orderColumns);
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    setItems(
      orderColumns.sort((a, b) => (a.columnOrder ?? 0) - (b.columnOrder ?? 0))
    );
  }, [orderColumns]);

  const ref = useRef(null);
  const { t } = useTranslation('table');

  const {
    register,
    unregister,
    handleSubmit,
    getValues,
    reset,
    formState: { isValid, errors },
  } = useForm<ColumnNamesFormValues>({ mode: 'onChange' });

  const onSubmitInternal = handleSubmit(async () => {
    setIsSubmitting(true);
    await onSubmit(
      items.map((item, index) => ({
        ...item,
        columnType: item.columnType ?? ColumnType.Custom,
        columnOrder: index,
        id: item.id,
      }))
    );
    setIsSubmitting(false);
    onClose();
  });

  const deleteCustomColumnInternal = async (columnId: string) => {
    invariant(
      deleteCustomColumn,
      'Columns cannot be deleted without supplying delete function'
    );
    if (orderColumns.find((e) => e.id === columnId)) {
      await deleteCustomColumn(columnId);
    } else {
      setItems(items.filter((e) => e.id !== columnId));
    }
  };

  const onCloseInternal = () => {
    reset();
    setItems([...orderColumns]);
    setEditingColumns([]);
    onClose();
  };

  const toggleVisibleInternal = (item: Column) => {
    const index = items.findIndex((e) => e.id === item.id);
    const newItems = [...items];
    newItems[index] = {
      ...items[index],
      isVisible: !item.isVisible,
    };
    setItems(newItems);
  };

  const [white, gray50, gray700, gray800] = useToken('colors', [
    'white',
    'gray.50',
    'gray.700',
    'gray.800',
  ]);
  const { colorMode } = useColorMode();

  const variants = {
    notDragging: {
      zIndex: 0,
      background: colorMode === 'light' ? white : gray800,
    },
    dragging: {
      zIndex: 1,
      background: colorMode === 'light' ? gray50 : gray700,
      boxShadow: 'md',
    },
  };

  interface ColumnNameFormValues {
    name: string;
  }
  interface ColumnNamesFormValues {
    [columnId: string]: ColumnNameFormValues;
  }

  const [editingColumns, setEditingColumns] = useState<string[]>([]);

  // Helper to get display name for a column
  const getColumnDisplayName = (item: Column) => {
    const mappedName =
      columnNameMapping && columnNameMapping[item.id]
        ? columnNameMapping[item.id].newName
        : item.name;

    return getTranslatedDefaultColumnName(mappedName, t);
  };

  return (
    <Modal isOpen={isOpen} onClose={onCloseInternal}>
      <ModalOverlay />
      <ModalContent ref={ref}>
        <ModalHeader>{t('column-management-modal.title')}</ModalHeader>
        <ModalCloseButton />
        <Divider orientation="horizontal" />
        <ModalBody>
          <>
            <Flex mb={2} ml={2} justifyContent={'flex-start'}>
              <PlaceholderText>
                {t('column-management-modal.columns')}
              </PlaceholderText>
            </Flex>
            <Divider />
          </>
          <List
            as={Reorder.Group}
            spacing={2}
            axis="y"
            values={items}
            onReorder={setItems}
            layoutScroll
            style={{ overflow: 'auto' }}
            maxHeight="60vh"
          >
            <form onSubmit={onSubmitInternal} id="edit-column-form">
              {items.map((item) =>
                editingColumns.includes(item.id) ? (
                  <HStack key={item.id} paddingX={2} alignItems="center">
                    <FormControl isRequired>
                      <Popover
                        placement="top-end"
                        isOpen={errors?.[item.id]?.name?.type === 'validName'}
                        autoFocus={false}
                        isLazy
                      >
                        <PopoverAnchor>
                          <Input
                            {...register(`${item.id}.name`, {
                              validate: {
                                notEmpty: (value) => !isEmpty(value),
                                validName: (value) => {
                                  if (
                                    items.some(
                                      (column) =>
                                        column.name.toLowerCase() ===
                                          value.toLowerCase() &&
                                        column.id !== item.id
                                    )
                                  ) {
                                    return t(
                                      'column-management-modal.validation.duplicate'
                                    );
                                  } else if (value.includes('$')) {
                                    return t(
                                      'column-management-modal.validation.invalid-character'
                                    );
                                  }
                                },
                              },
                            })}
                            placeholder="Property name"
                            form="edit-column-form"
                            defaultValue={getColumnDisplayName(item)}
                          />
                        </PopoverAnchor>
                        <PopoverContent>
                          <Alert status="error" fontSize="sm">
                            <AlertIcon />
                            <AlertTitle>
                              {errors?.[item.id]?.name?.message}
                            </AlertTitle>
                          </Alert>
                        </PopoverContent>
                      </Popover>
                    </FormControl>
                    <IconButton
                      aria-label="Cancel"
                      colorScheme="gray"
                      size="xs"
                      icon={<SparkelIcon icon={faTimes} />}
                      onClick={() => {
                        unregister(`${item.id}.name`);
                        setEditingColumns(
                          editingColumns.filter((e) => e !== item.id)
                        );
                        if (item.name === '') {
                          setItems(items.filter((e) => e.id !== item.id));
                        }
                      }}
                    />
                    <IconButton
                      aria-label="Save"
                      size="xs"
                      isDisabled={!isValid}
                      icon={
                        <SparkelIcon
                          icon={faCheck}
                          color={['gray.50', 'gray.700']}
                        />
                      }
                      onClick={() => {
                        item.name = getValues(`${item.id}.name`);
                        setEditingColumns(
                          editingColumns.filter((e) => e !== item.id)
                        );
                      }}
                    />
                  </HStack>
                ) : (
                  <ListItem
                    key={item.id}
                    as={Reorder.Item}
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    value={item}
                    p={2}
                    bg="gray.100"
                    rounded="xl"
                    dragTransition={{
                      bounceStiffness: 600,
                    }}
                    variants={variants}
                    initial="notDragging"
                    whileDrag="dragging"
                    position="relative"
                    cursor={editingColumns.length === 0 ? 'move' : undefined}
                    drag={editingColumns.length === 0 ? 'y' : false}
                  >
                    <Flex
                      justifyContent={'flex-start'}
                      color={
                        item.isVisible ? undefined : ['gray.300', 'gray.500']
                      }
                    >
                      <Box mr={2}>
                        <SparkelIcon
                          icon={faGripVertical}
                          color={['gray.300', 'gray.500']}
                        />
                      </Box>
                      <Box>{getColumnDisplayName(item)}</Box>
                      <IconButton
                        aria-label={`${
                          item.isVisible ? 'Hide' : 'Show'
                        } column ${getColumnDisplayName(item)}`}
                        variant="link"
                        ml="auto"
                        icon={
                          <SparkelIcon
                            icon={item.isVisible ? faEye : faEyeSlash}
                            color={
                              item.isVisible
                                ? undefined
                                : ['gray.300', 'gray.500']
                            }
                          />
                        }
                        onClick={() => toggleVisibleInternal(item)}
                      ></IconButton>
                      {deleteCustomColumn && (
                        // If this Menu is lazy, the delete modal will not be able to open
                        <Menu placement="end">
                          <Tooltip
                            label={t('column-management-modal.more-settings')}
                          >
                            <MenuButton
                              aria-label="More settings"
                              as={IconButton}
                              size={'xs'}
                              maxWidth={16}
                              colorScheme="gray"
                            >
                              <SparkelIcon icon={faEllipsisH} />
                            </MenuButton>
                          </Tooltip>
                          <Portal containerRef={ref}>
                            <MenuList zIndex={2}>
                              <MenuItem
                                aria-label="Rename"
                                icon={<SparkelIcon icon={faPen} fixedWidth />}
                                onClick={() =>
                                  setEditingColumns([
                                    ...editingColumns,
                                    item.id,
                                  ])
                                }
                              >
                                {t('column-management-modal.menu-item.rename')}
                              </MenuItem>
                              <DeleteMenuItem
                                isDisabled={
                                  item.columnType != ColumnType.Custom
                                }
                                name={getColumnDisplayName(item)}
                                onDelete={() =>
                                  deleteCustomColumnInternal(item.id)
                                }
                              />
                            </MenuList>
                          </Portal>
                        </Menu>
                      )}
                    </Flex>
                  </ListItem>
                )
              )}
            </form>
          </List>
        </ModalBody>
        <Divider orientation="horizontal" mt={1} />
        <ModalFooter gap={2} justifyContent="flex-start">
          {resetColumnWidths && (
            <Button
              variant="link"
              colorScheme="gray"
              onClick={resetColumnWidths}
            >
              {t('column-management-modal.reset-widths')}
            </Button>
          )}
          <ButtonGroup ml="auto">
            <Button
              colorScheme="brand"
              type="submit"
              form="edit-column-form"
              isLoading={isSubmitting}
              isDisabled={!isValid || editingColumns.length > 0}
            >
              {t('column-management-modal.confirm')}
            </Button>
            <Button colorScheme={'gray'} onClick={onCloseInternal}>
              {t('column-management-modal.cancel')}
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});
